import React, { useState, useEffect, useCallback } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB, s_GB, s_GC, s_TI } from '../../shared/styles';
import { notifyUser } from '../../shared/dbService';

const BASE = process.env.REACT_APP_API_URL || '/api';

const GAMES = [
  { id: 'memory',          label: 'Memory',           labelNL: 'Geheugen',        icon: '🧠' },
  { id: 'reaction',        label: 'Reaction',          labelNL: 'Reactie',         icon: '⚡' },
  { id: 'sequence',        label: 'Sequence',          labelNL: 'Reeks',           icon: '🔢' },
  { id: 'stroop',          label: 'Stroop',            labelNL: 'Stroop',          icon: '🎨' },
  { id: 'math',            label: 'Math',              labelNL: 'Rekenen',         icon: '➕' },
  { id: 'focustap',        label: 'Focus Tap',         labelNL: 'Focus Tap',       icon: '🎯' },
  { id: 'oddsymbol',       label: 'Odd Symbol',        labelNL: 'Vreemd Symbool',  icon: '🔍' },
  { id: 'directionflash',  label: 'Direction Flash',   labelNL: 'Richting Flash',  icon: '🧭' },
  { id: 'peripheralflash', label: 'Peripheral Flash',  labelNL: 'Perifeer Flash',  icon: '👁' },
  { id: 'numchain',        label: 'Number Chain',      labelNL: 'Cijferketen',     icon: '🔗' },
  { id: 'wordmemory',      label: 'Word Memory',       labelNL: 'Woordgeheugen',   icon: '📝' },
];

const WEEKDAYS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAYS_NL = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const MEDALS      = ['🥇', '🥈', '🥉'];
const PT_LABEL    = [7, 5, 3, 2, 1];

function randomSchedule() {
  const pool = [...GAMES].sort(() => Math.random() - 0.5);
  return Array.from({ length: 7 }, (_, i) => pool[i % pool.length].id);
}

function fmt(n) { return n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n); }

function Avatar({ user, size = 32 }) {
  const [err, setErr] = useState(false);
  const col = user?.color || '#C8A84B';
  if (user?.picture && !err) {
    return (
      <img src={user.picture} alt={user.name} onError={() => setErr(true)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${col}55`, flexShrink: 0 }} />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: `${col}22`, border: `2px solid ${col}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 700, color: col, flexShrink: 0 }}>
      {(user?.name || '?').slice(0, 2).toUpperCase()}
    </div>
  );
}

function StatusBadge({ status, nl }) {
  const cfg = {
    active:   { label: nl ? '🟢 LIVE'      : '🟢 LIVE',     bg: '#1a3a1a', border: '#2A9E82', color: '#2A9E82' },
    upcoming: { label: nl ? '🕐 BINNENKORT' : '🕐 UPCOMING', bg: '#1a1a30', border: '#7B6FD0', color: '#7B6FD0' },
    ended:    { label: nl ? '✓ AFGELOPEN'  : '✓ ENDED',     bg: '#1a1a1a', border: '#555',    color: '#999'    },
  }[status] || {};
  return (
    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, padding: '3px 8px', borderRadius: 20, background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

function ProgressBar({ value, max, color = _T.accent }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ height: 6, borderRadius: 3, background: `${_T.cardBorderBot}`, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${pct}%`, borderRadius: 3, background: `linear-gradient(90deg, ${color}, ${color}bb)`, transition: 'width 0.5s ease' }} />
    </div>
  );
}

// ── Competition List Card ──────────────────────────────────────────────────────

function CompetitionCard({ comp, currentUserId, myStanding, nl, onView, onJoin, onLeave }) {
  const isActive   = comp.status === 'active';
  const isUpcoming = comp.status === 'upcoming';
  const isEnded    = comp.status === 'ended';
  const daysPast   = isActive
    ? Math.round((Date.now() - new Date(comp.startDate).getTime()) / 86400000) + 1
    : isEnded ? 7 : 0;

  const startFmt = new Date(comp.startDate).toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const endDate  = new Date(new Date(comp.startDate).getTime() + 6 * 86400000);
  const endFmt   = endDate.toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'short' });
  const daysLeft = Math.max(0, Math.round((endDate.getTime() - Date.now()) / 86400000));

  const todayGame = isActive && comp.gameSchedule
    ? comp.gameSchedule[Math.min(daysPast - 1, 6)]
    : null;

  const accentCol = isActive ? '#2A9E82' : isUpcoming ? '#7B6FD0' : '#555';

  return (
    <div style={{ background: _T.bg2, border: `1px solid ${isActive ? '#2A9E82' : _T.cardBorderTop}`, borderRadius: 18, marginBottom: 12, overflow: 'hidden' }}>
      {/* Top stripe */}
      {isActive && <div style={{ height: 3, background: `linear-gradient(90deg, #2A9E82, #C8A84B)` }} />}
      {isUpcoming && <div style={{ height: 3, background: `linear-gradient(90deg, #7B6FD0, #2ABFCF)` }} />}

      <div style={{ padding: '16px 16px 12px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 28 }}>⚔️</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={{ color: C.white, fontSize: 15, fontWeight: 700 }}>{comp.name}</span>
              <StatusBadge status={comp.status} nl={nl} />
            </div>
            {comp.description && (
              <div style={{ color: C.silver, fontSize: 11, lineHeight: 1.4, marginBottom: 4 }}>
                {comp.description.slice(0, 80)}{comp.description.length > 80 ? '…' : ''}
              </div>
            )}
            <div style={{ color: C.silver, fontSize: 11 }}>
              📅 {startFmt} → {endFmt}
              {isActive && <span style={{ color: '#2A9E82', fontWeight: 600 }}> · {nl ? `${daysLeft}d over` : `${daysLeft}d left`}</span>}
              <span style={{ marginLeft: 8 }}>👥 {comp.participantCount}</span>
              {myStanding && <span style={{ marginLeft: 8, color: _T.accent, fontWeight: 600 }}> · #{myStanding.rank} · {myStanding.totalPoints}pts</span>}
            </div>
          </div>
        </div>

        {/* Active: progress + today's game */}
        {isActive && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: C.silver, fontSize: 10 }}>{nl ? `Dag ${daysPast} van 7` : `Day ${daysPast} of 7`}</span>
              <span style={{ color: '#2A9E82', fontSize: 10, fontWeight: 600 }}>{Math.round((daysPast / 7) * 100)}%</span>
            </div>
            <ProgressBar value={daysPast} max={7} color="#2A9E82" />
            {todayGame && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, background: `${_T.accent}10`, border: `1px solid ${_T.accent}33`, borderRadius: 10, padding: '6px 10px' }}>
                <span style={{ fontSize: 18 }}>{todayGame.icon || '🎮'}</span>
                <span style={{ color: _T.accent, fontSize: 12, fontWeight: 600 }}>
                  {nl ? 'Vandaag: ' : "Today's game: "}{nl ? todayGame.labelNL || todayGame.label : todayGame.label}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Upcoming: join/leave */}
        {isUpcoming && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ color: C.silver, fontSize: 12 }}>
              {nl ? `Start: ${startFmt}` : `Starts: ${startFmt}`}
            </span>
          </div>
        )}

        {/* Ended: winner */}
        {isEnded && myStanding && (
          <div style={{ color: C.silver, fontSize: 12, marginBottom: 8 }}>
            {nl ? 'Jouw eindstand' : 'Your final rank'}: <span style={{ color: _T.accent, fontWeight: 700 }}>#{myStanding.rank} · {myStanding.totalPoints}pts</span>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          {(isActive || isEnded) && (
            <button onClick={onView} style={s_PB({ flex: 1, padding: '9px 0', fontSize: 12 })}>
              {isActive ? (nl ? '⚔️ Bekijk & Speel' : '⚔️ View & Play') : (nl ? '🏆 Resultaten' : '🏆 Results')}
            </button>
          )}
          {isUpcoming && (
            <>
              {comp.iJoined
                ? <button onClick={onLeave} style={s_GB({ flex: 1, padding: '9px 0', fontSize: 12 })}>✓ {nl ? 'Deelnemend' : 'Joined'}</button>
                : <button onClick={onJoin}  style={s_PB({ flex: 1, padding: '9px 0', fontSize: 12 })}>+ {nl ? 'Deelnemen' : 'Join'}</button>}
              <button onClick={onView} style={s_GB({ padding: '9px 14px', fontSize: 12 })}>›</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Competition Detail ────────────────────────────────────────────────────────

function CompetitionDetail({ groupId, compId, currentUserId, userName, isAdmin, lang, nl, onBack, onPlayGame }) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [rankAlert, setRankAlert] = useState(null); // {prev, curr, dir}

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${BASE}/groups/${groupId}/competitions/${compId}?uid=${encodeURIComponent(currentUserId)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && !d.error) {
          setDetail(d);
          // Rank change detection
          try {
            const myS = (d.standings || []).find(s => s.userId === currentUserId);
            if (myS) {
              const key  = `sl_comp_rank_${compId}`;
              const prev = localStorage.getItem(key);
              const curr = String(myS.rank);
              if (prev && prev !== curr) {
                const prevN = parseInt(prev, 10);
                setRankAlert({ prev: prevN, curr: myS.rank, dir: myS.rank < prevN ? 'up' : 'down' });
              }
              localStorage.setItem(key, curr);
            }
          } catch {}
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [groupId, compId, currentUserId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>⏳</div>;
  if (!detail) return <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>{nl ? 'Niet gevonden' : 'Not found'}</div>;

  const { name, description, gameSchedule, status, standings, completedDays, currentDay, todayGameId, todayGame, myTodayScore, iJoined } = detail;
  const daysPast   = status === 'active' ? Math.min((currentDay ?? 0) + 1, 7) : status === 'ended' ? 7 : 0;
  const startDate  = new Date(detail.startDate);
  const myStanding = standings.find(s => s.userId === currentUserId);
  const leader     = standings[0];

  const DAYS = nl ? WEEKDAYS_NL : WEEKDAYS_EN;

  const toggleJoin = async () => {
    const method = iJoined ? 'DELETE' : 'POST';
    const url    = `${BASE}/groups/${groupId}/competitions/${compId}/join${iJoined ? `?uid=${encodeURIComponent(currentUserId)}` : ''}`;
    await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: iJoined ? null : JSON.stringify({ uid: currentUserId }),
    });
    load();
  };

  const deleteComp = async () => {
    if (!window.confirm(nl ? 'Competitie verwijderen?' : 'Delete this competition?')) return;
    await fetch(`${BASE}/groups/${groupId}/competitions/${compId}`, { method: 'DELETE' });
    onBack();
  };

  return (
    <div className="slideIn">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13 }}>
          ← {nl ? 'Terug' : 'Back'}
        </button>
        {isAdmin && (
          <button onClick={deleteComp} style={{ background: 'none', border: 'none', color: '#ff4444', cursor: 'pointer', fontSize: 13 }}>🗑️</button>
        )}
      </div>

      {/* Rank change alert */}
      {rankAlert && (
        <div onClick={() => setRankAlert(null)} style={{ display: 'flex', alignItems: 'center', gap: 10, background: rankAlert.dir === 'up' ? '#0d2a1a' : '#2a0d0d', border: `1px solid ${rankAlert.dir === 'up' ? '#2A9E82' : '#C05050'}`, borderRadius: 14, padding: '10px 14px', marginBottom: 14, cursor: 'pointer' }}>
          <span style={{ fontSize: 22 }}>{rankAlert.dir === 'up' ? '📈' : '📉'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: rankAlert.dir === 'up' ? '#2A9E82' : '#C05050', fontSize: 13, fontWeight: 700 }}>
              {rankAlert.dir === 'up'
                ? (nl ? `Je bent gestegen naar #${rankAlert.curr}!` : `You moved up to #${rankAlert.curr}!`)
                : (nl ? `Je bent gezakt naar #${rankAlert.curr}` : `You dropped to #${rankAlert.curr}`)}
            </div>
            <div style={{ color: C.silver, fontSize: 11, marginTop: 2 }}>
              {nl ? `Was #${rankAlert.prev} · Tik om te sluiten` : `Was #${rankAlert.prev} · Tap to dismiss`}
            </div>
          </div>
        </div>
      )}

      {/* Hero header */}
      <div style={{ background: _T.bg2, border: `1px solid ${status === 'active' ? '#2A9E82' : _T.cardBorderTop}`, borderRadius: 18, padding: '20px 18px', marginBottom: 14, overflow: 'hidden', position: 'relative' }}>
        {status === 'active' && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg,#2A9E82,#C8A84B)' }} />}
        {status === 'upcoming' && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg,#7B6FD0,#2ABFCF)' }} />}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 36, lineHeight: 1 }}>⚔️</div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              <span style={{ color: C.white, fontSize: 18, fontWeight: 700 }}>{name}</span>
              <StatusBadge status={status} nl={nl} />
            </div>
            {description && <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.5 }}>{description}</div>}
          </div>
        </div>
        {status === 'active' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: C.silver, fontSize: 11 }}>
                {nl ? `Dag ${daysPast} van 7` : `Day ${daysPast} of 7`} · {detail.participantCount} {nl ? 'deelnemers' : 'competitors'}
              </span>
              <span style={{ color: '#2A9E82', fontSize: 11, fontWeight: 700 }}>{Math.round((daysPast / 7) * 100)}%</span>
            </div>
            <ProgressBar value={daysPast} max={7} color="#2A9E82" />
          </div>
        )}
        {status === 'upcoming' && (
          <div style={{ color: C.silver, fontSize: 12 }}>
            📅 {nl ? 'Start' : 'Starts'}: {startDate.toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            <span style={{ marginLeft: 12 }}>👥 {detail.participantCount} {nl ? 'aangemeld' : 'signed up'}</span>
          </div>
        )}
        {status === 'ended' && (
          <div style={{ color: C.silver, fontSize: 12 }}>
            {nl ? 'Afgelopen · Eindstand:' : 'Ended · Final standings:'} {leader ? `🥇 ${leader.name} (${leader.totalPoints}pts)` : '—'}
          </div>
        )}
        {/* Join/Leave for upcoming */}
        {status === 'upcoming' && (
          <button onClick={toggleJoin} style={iJoined ? s_GB({ marginTop: 12, width: '100%' }) : s_PB({ marginTop: 12, width: '100%' })}>
            {iJoined ? `✓ ${nl ? 'Deelnemend — klik om af te melden' : 'Joined — click to leave'}` : `+ ${nl ? 'Deelnemen aan competitie' : 'Join this competition'}`}
          </button>
        )}
      </div>

      {/* TODAY'S CHALLENGE — active only */}
      {status === 'active' && todayGame && iJoined && (
        <div style={{ background: `linear-gradient(135deg, ${_T.bg2}, ${_T.bg3})`, border: `1px solid ${_T.accent}55`, borderRadius: 18, padding: '18px 18px', marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>
            {nl ? "TODAY'S CHALLENGE" : "TODAY'S CHALLENGE"} · {nl ? `DAG ${daysPast}` : `DAY ${daysPast}`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: `${_T.accent}18`, border: `2px solid ${_T.accent}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, flexShrink: 0 }}>
              {todayGame.icon}
            </div>
            <div>
              <div style={{ color: C.white, fontSize: 18, fontWeight: 700 }}>{nl ? todayGame.labelNL || todayGame.label : todayGame.label}</div>
              {myTodayScore !== null
                ? <div style={{ color: '#2A9E82', fontSize: 13, fontWeight: 600, marginTop: 4 }}>✓ {nl ? 'Gespeeld vandaag' : 'Played today'}: <span style={{ color: C.white }}>{fmt(myTodayScore)} pts</span></div>
                : <div style={{ color: C.silver, fontSize: 12, marginTop: 4 }}>{nl ? 'Nog niet gespeeld vandaag' : 'Not played today yet'}</div>}
              {leader && leader.userId !== currentUserId && (
                <div style={{ color: C.silver, fontSize: 11, marginTop: 3 }}>
                  🥇 {leader.name}: {leader.dayScores?.[currentDay] !== undefined ? `${fmt(leader.dayScores[currentDay])} pts` : (nl ? 'nog niet gespeeld' : 'not played yet')}
                </div>
              )}
            </div>
          </div>
          <button onClick={() => onPlayGame && onPlayGame(todayGameId)} style={{ ...s_PB({ width: '100%', fontSize: 14 }), background: `linear-gradient(135deg, ${_T.grad1}, ${_T.grad2})` }}>
            🎮 {nl ? `Speel ${todayGame.labelNL || todayGame.label} nu →` : `Play ${todayGame.label} Now →`}
          </button>
          <div style={{ color: `${C.silver}77`, fontSize: 10, textAlign: 'center', marginTop: 8 }}>
            {nl ? 'Jouw beste score van vandaag telt automatisch mee' : 'Your best score today counts automatically'}
          </div>
        </div>
      )}

      {/* WEEK SCHEDULE */}
      <div style={s_GC({ marginBottom: 14 })}>
        <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>
          {nl ? 'WEEKSCHEMA' : 'WEEK SCHEDULE'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {(gameSchedule || []).map((g, i) => {
            const date    = new Date(startDate.getTime() + i * 86400000);
            const isToday = status === 'active' && i === currentDay;
            const isPast  = i < completedDays;
            const isFuture = !isPast && !isToday;
            const myScore = myStanding?.dayScores?.[i];
            const myRank  = myStanding?.dayRanks?.[i];

            return (
              <div key={i} style={{
                background: isToday ? `${_T.accent}20` : isPast ? `${_T.bg4}` : 'transparent',
                border: `1px solid ${isToday ? _T.accent : isPast ? _T.cardBorderBot : _T.cardBorderBot + '55'}`,
                borderRadius: 12, padding: '8px 4px', textAlign: 'center',
                opacity: isFuture ? 0.5 : 1,
              }}>
                <div style={{ fontSize: 9, color: isToday ? _T.accent : C.silver, fontWeight: 700, marginBottom: 4 }}>
                  {DAYS[i]}
                </div>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{g.icon || '🎮'}</div>
                <div style={{ fontSize: 8, color: C.silver, marginBottom: 3, lineHeight: 1.2 }}>
                  {nl ? g.labelNL || g.label : g.label}
                </div>
                {isPast && myRank && (
                  <div style={{ fontSize: 12 }}>{MEDALS[myRank - 1] || `#${myRank}`}</div>
                )}
                {isPast && !myRank && (
                  <div style={{ fontSize: 10, color: `${C.silver}66` }}>—</div>
                )}
                {isToday && (
                  <div style={{ fontSize: 9, color: _T.accent, fontWeight: 700 }}>TODAY</div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
          {PT_LABEL.map((pts, i) => (
            <span key={i} style={{ fontSize: 10, color: C.silver }}>
              {MEDALS[i] || `#${i + 1}`} = {pts}pts
            </span>
          ))}
          <span style={{ fontSize: 10, color: C.silver }}>5th+ = 1pt</span>
        </div>
      </div>

      {/* STANDINGS */}
      <div style={s_GC({ marginBottom: 14 })}>
        <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>
          🏆 {nl ? 'KLASSEMENT' : 'STANDINGS'}
        </div>
        {standings.length === 0 && (
          <div style={{ textAlign: 'center', color: C.silver, fontSize: 12, padding: 16 }}>
            {nl ? 'Nog geen deelnemers' : 'No competitors yet'}
          </div>
        )}
        {standings.map((s, idx) => {
          const isMe = s.userId === currentUserId;
          return (
            <div key={s.userId} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
              borderBottom: idx < standings.length - 1 ? `1px solid ${_T.cardBorderBot}` : 'none',
              background: isMe ? `${_T.accent}08` : 'transparent',
              borderRadius: isMe ? 10 : 0, paddingLeft: isMe ? 8 : 0, paddingRight: isMe ? 8 : 0,
            }}>
              <div style={{ width: 28, textAlign: 'center', fontSize: 16, flexShrink: 0 }}>
                {MEDALS[idx] || <span style={{ color: C.silver, fontSize: 12 }}>#{s.rank}</span>}
              </div>
              <Avatar user={s} size={34} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: isMe ? _T.accent : C.white, fontSize: 13, fontWeight: isMe ? 700 : 500 }}>
                    {s.name}{isMe ? (nl ? ' (jij)' : ' (you)') : ''}
                  </span>
                </div>
                {/* Daily breakdown */}
                <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                  {(gameSchedule || []).map((g, d) => {
                    const pts   = s.dayPoints?.[d];
                    const score = s.dayScores?.[d];
                    const rank  = s.dayRanks?.[d];
                    const isPast = d < completedDays;
                    if (!isPast) return (
                      <div key={d} style={{ width: 22, height: 22, borderRadius: 6, background: `${_T.bg4}`, border: `1px solid ${_T.cardBorderBot}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: `${C.silver}55` }}>
                        {g.icon}
                      </div>
                    );
                    return (
                      <div key={d} title={score !== undefined ? `${fmt(score)} pts` : nl ? 'Niet gespeeld' : 'Not played'}
                        style={{ width: 22, height: 22, borderRadius: 6, background: rank ? (rank === 1 ? '#C8A84B22' : rank === 2 ? '#88888822' : '#C8602022') : `${_T.bg4}`, border: `1px solid ${rank === 1 ? '#C8A84B' : rank === 2 ? '#888' : rank === 3 ? '#C86020' : _T.cardBorderBot}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9 }}>
                        {rank ? (MEDALS[rank - 1] || `${pts}`) : '—'}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ color: isMe ? _T.accent : C.white, fontSize: 15, fontWeight: 700 }}>{s.totalPoints}</div>
                <div style={{ color: C.silver, fontSize: 10 }}>pts</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Create Competition Form ───────────────────────────────────────────────────

function CreateForm({ groupId, currentUserId, userName, lang, nl, onCreated, onCancel }) {
  const [form, setForm] = useState({ name: '', description: '', startDate: '' });
  const [schedule, setSchedule] = useState(randomSchedule());
  const [saving, setSaving] = useState(false);

  const minDate = new Date(); minDate.setDate(minDate.getDate() + 1);
  const minStr  = minDate.toISOString().split('T')[0];

  const startDateObj = form.startDate ? new Date(form.startDate) : null;

  const create = async () => {
    if (!form.name.trim() || !form.startDate) return;
    setSaving(true);
    const r = await fetch(`${BASE}/groups/${groupId}/competitions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: form.name, description: form.description, startDate: form.startDate, gameSchedule: schedule, uid: currentUserId, creatorName: userName }),
    });
    if (r.ok) { const d = await r.json(); onCreated(d.id); }
    else setSaving(false);
  };

  return (
    <div style={s_GC({ marginBottom: 14 })}>
      <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 16 }}>⚔️ {nl ? 'NIEUWE COMPETITIE' : 'NEW COMPETITION'}</div>

      <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
        placeholder={nl ? 'Naam competitie *' : 'Competition name *'} style={s_TI({ marginBottom: 10 })} />
      <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
        placeholder={nl ? 'Omschrijving (optioneel)' : 'Description (optional)'} style={s_TI({ minHeight: 60, resize: 'none', marginBottom: 10 })} />

      <div style={{ marginBottom: 10 }}>
        <label style={{ color: C.silver, fontSize: 11, marginBottom: 4, display: 'block' }}>{nl ? 'Startdatum *' : 'Start date *'}</label>
        <input type="date" min={minStr} value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })}
          style={s_TI({})} />
      </div>

      {/* Game schedule */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ color: C.silver, fontSize: 11 }}>{nl ? 'Spel per dag' : 'Game per day'}</span>
          <button onClick={() => setSchedule(randomSchedule())} style={{ background: `${_T.accent}18`, border: `1px solid ${_T.accent}44`, color: _T.accent, borderRadius: 20, padding: '4px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
            🎲 {nl ? 'Willekeurig' : 'Randomize'}
          </button>
        </div>
        {(nl ? WEEKDAYS_NL : WEEKDAYS_EN).map((day, i) => {
          const date = startDateObj
            ? new Date(startDateObj.getTime() + i * 86400000).toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'short' })
            : '';
          const selGame = GAMES.find(g => g.id === schedule[i]);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: `1px solid ${_T.cardBorderBot}` }}>
              <div style={{ width: 46, flexShrink: 0 }}>
                <div style={{ color: C.white, fontSize: 12, fontWeight: 600 }}>{day}</div>
                {date && <div style={{ color: `${C.silver}88`, fontSize: 9 }}>{date}</div>}
              </div>
              <div style={{ fontSize: 20 }}>{selGame?.icon || '🎮'}</div>
              <select value={schedule[i]} onChange={e => { const s2 = [...schedule]; s2[i] = e.target.value; setSchedule(s2); }}
                style={{ ...s_TI({ flex: 1, padding: '6px 8px', marginBottom: 0 }), appearance: 'none' }}>
                {GAMES.map(g => (
                  <option key={g.id} value={g.id}>{g.icon} {nl ? g.labelNL : g.label}</option>
                ))}
              </select>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={create} disabled={!form.name.trim() || !form.startDate || saving}
          style={s_PB({ flex: 1, opacity: form.name.trim() && form.startDate && !saving ? 1 : 0.4 })}>
          {saving ? '...' : (nl ? '⚔️ Aanmaken' : '⚔️ Create Competition')}
        </button>
        <button onClick={onCancel} style={s_GB({ padding: '12px 16px' })}>✕</button>
      </div>
    </div>
  );
}

// ── Main CompetitionsTab ──────────────────────────────────────────────────────

export default function CompetitionsTab({ groupId, currentUserId, userName, isAdmin, lang, onPlayGame }) {
  const nl = lang === 'nl';
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showCreate, setShowCreate]     = useState(false);
  const [selectedId, setSelectedId]     = useState(null);

  const load = useCallback(() => {
    if (!groupId || !currentUserId) return;
    setLoading(true);
    fetch(`${BASE}/groups/${groupId}/competitions?uid=${encodeURIComponent(currentUserId)}`)
      .then(r => r.ok ? r.json() : [])
      .then(d => { if (Array.isArray(d)) setCompetitions(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [groupId, currentUserId]);

  useEffect(() => { load(); }, [load]);

  const join = async (compId) => {
    await fetch(`${BASE}/groups/${groupId}/competitions/${compId}/join`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: currentUserId }),
    });
    load();
  };

  const leave = async (compId) => {
    await fetch(`${BASE}/groups/${groupId}/competitions/${compId}/join?uid=${encodeURIComponent(currentUserId)}`, { method: 'DELETE' });
    load();
  };

  if (selectedId) {
    return (
      <CompetitionDetail
        groupId={groupId} compId={selectedId} currentUserId={currentUserId}
        userName={userName} isAdmin={isAdmin} lang={lang} nl={nl}
        onBack={() => { setSelectedId(null); load(); }}
        onPlayGame={onPlayGame}
      />
    );
  }

  const active   = competitions.filter(c => c.status === 'active');
  const upcoming = competitions.filter(c => c.status === 'upcoming');
  const ended    = competitions.filter(c => c.status === 'ended');

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div style={{ color: C.white, fontSize: 15, fontWeight: 600 }}>⚔️ {nl ? 'Competities' : 'Competitions'}</div>
          <div style={{ color: C.silver, fontSize: 11, marginTop: 2 }}>{nl ? 'Eén spel per dag, één week lang' : 'One game per day, one week long'}</div>
        </div>
        {isAdmin && (
          <button onClick={() => setShowCreate(!showCreate)} style={s_PB({ padding: '8px 14px', fontSize: 12 })}>
            {showCreate ? '✕' : '+ ' + (nl ? 'Nieuw' : 'New')}
          </button>
        )}
      </div>

      {showCreate && (
        <CreateForm
          groupId={groupId} currentUserId={currentUserId} userName={userName}
          lang={lang} nl={nl}
          onCreated={id => { setShowCreate(false); load(); setSelectedId(id); }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {loading && <div style={{ textAlign: 'center', color: C.silver, padding: 32 }}>⏳</div>}

      {!loading && competitions.length === 0 && !showCreate && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 32 }), color: C.silver }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚔️</div>
          <div style={{ color: C.white, fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
            {nl ? 'Nog geen competities' : 'No competitions yet'}
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
            {isAdmin
              ? (nl ? 'Maak een weekcompetitie aan en daag je groep uit!' : 'Create a week competition and challenge your group!')
              : (nl ? 'Wacht tot een admin een competitie aanmaakt.' : 'Wait for an admin to create a competition.')}
          </div>
        </div>
      )}

      {active.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 10, color: '#2A9E82', letterSpacing: 2, fontWeight: 700, marginBottom: 8 }}>🟢 {nl ? 'ACTIEF' : 'LIVE'}</div>
          {active.map(c => (
            <CompetitionCard key={c.id} comp={c} currentUserId={currentUserId} nl={nl}
              onView={() => setSelectedId(c.id)}
              onJoin={() => join(c.id)} onLeave={() => leave(c.id)} />
          ))}
        </div>
      )}

      {upcoming.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 10, color: '#7B6FD0', letterSpacing: 2, fontWeight: 700, marginBottom: 8 }}>🕐 {nl ? 'BINNENKORT' : 'UPCOMING'}</div>
          {upcoming.map(c => (
            <CompetitionCard key={c.id} comp={c} currentUserId={currentUserId} nl={nl}
              onView={() => setSelectedId(c.id)}
              onJoin={() => join(c.id)} onLeave={() => leave(c.id)} />
          ))}
        </div>
      )}

      {ended.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, fontWeight: 700, marginBottom: 8 }}>✓ {nl ? 'AFGELOPEN' : 'ENDED'}</div>
          {ended.map(c => (
            <CompetitionCard key={c.id} comp={c} currentUserId={currentUserId} nl={nl}
              onView={() => setSelectedId(c.id)}
              onJoin={() => join(c.id)} onLeave={() => leave(c.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
